import { useCallback, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Grid } from "antd";
import { ItemType } from "antd/lib/menu/interface";
import {
  DesktopOutlined,
  ContainerOutlined,
  LikeOutlined,
  ScanOutlined,
  IdcardOutlined,
  PlusOutlined,
  TeamOutlined,
  DollarCircleOutlined,
  ReadOutlined,
  AudioMutedOutlined,
  FileDoneOutlined,
} from "@ant-design/icons";

import { Logo } from "@components/Logo";
import { useClientSettings } from "@hooks/useClientSettings";
import usePermissions from "@hooks/usePermissions";
import { featureFlags } from "@settings";

import { CorbanSelector } from "./CorbanSelector";
import { LogoLink, Menu, Footer, Text, Container, Content } from "./styles";
import { LOCAL_STORAGE } from "@constants";
import { isTrainingTabEnabled } from "../../settings/whiteLabel";

export const SidebarMenu = () => {
  const BENEFIT_CARD_NAME = useClientSettings().benefitCardName;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { routes, homeRoute } = usePermissions();

  const currentRoute = pathname.substring(1);
  const routeSplit = currentRoute.split("/");
  const currentSelectedSub1 = routeSplit[1];
  const currentSelectedSub2 = `${routeSplit[0]}/${routeSplit[1]}`;
  const hasJourney = localStorage.getItem(LOCAL_STORAGE.LAST_JOURNEY);

  const hasUsersCrud = featureFlags.enableUsersCrud;

  const [openKeys, setOpenKeys] = useState<string[]>([
    currentSelectedSub1,
    currentSelectedSub2,
  ]);

  const screens = Grid.useBreakpoint();

  const isAllowedItemType = useCallback(
    (itemType: ItemType) =>
      itemType?.key ? !!routes[`/${itemType.key}`] : true,
    [routes],
  );

  const getAllowedItemTypes = useCallback(
    (
      itemTypeList: ((ItemType & { children?: ItemType[] }) | null)[],
    ): ItemType[] => {
      return itemTypeList.reduce((allowedItemTypes: ItemType[], itemType) => {
        if (itemType && isAllowedItemType(itemType)) {
          if (itemType.children) {
            return [
              ...allowedItemTypes,
              {
                ...itemType,
                children: getAllowedItemTypes(itemType.children),
              },
            ];
          }

          return [...allowedItemTypes, itemType];
        }

        return allowedItemTypes;
      }, []);
    },
    [isAllowedItemType],
  );
  const menuItems = useMemo(() => {
    const fullMenu: ItemType[] = [
      { type: "divider" },
      {
        label: "Serviços",
        key: "servicos",
        icon: <ContainerOutlined style={{ color: "#8A92A6" }} />,
        children: [
          {
            label: "INSS",
            key: "inss",
            icon: <ScanOutlined style={{ color: "#8A92A6" }} />,
            children: [
              {
                label: "Aumento Salarial",
                key: "inss/aumento-margem",
                icon: <LikeOutlined style={{ color: "#8A92A6" }} />,
              },
              ...(featureFlags.enableBatchTypingMarginIncrease
                ? [
                    {
                      label: "Digitação em lote (Aumento de Margem)",
                      key: "inss/aumento-margem-lote",
                      icon: <LikeOutlined style={{ color: "#8A92A6" }} />,
                    },
                  ]
                : []),
              {
                label: "Margem Livre",
                key: "inss/margem-livre",
                icon: <LikeOutlined style={{ color: "#8A92A6" }} />,
              },
              {
                label: "Portabilidade",
                key: "inss/portabilidade",
                icon: <LikeOutlined style={{ color: "#8A92A6" }} />,
              },
              {
                label: "Portabilidade + Refin",
                key: "inss/portabilidade-refin",
                icon: <LikeOutlined style={{ color: "#8A92A6" }} />,
              },
              {
                label: "Refinanciamento",
                key: "inss/refinanciamento",
                icon: <LikeOutlined style={{ color: "#8A92A6" }} />,
              },
            ],
          },
          {
            label: BENEFIT_CARD_NAME,
            key: "cartao",
            icon: <IdcardOutlined style={{ color: "#8A92A6" }} />,
            children: [
              {
                label: BENEFIT_CARD_NAME,
                key: "cartao/cartao-beneficio",
                icon: <LikeOutlined style={{ color: "#8A92A6" }} />,
              },
              {
                label: "Agregação de Margem",
                key: "cartao/agregacao-margem",
                icon: <LikeOutlined style={{ color: "#8A92A6" }} />,
              },
            ],
          },
          (featureFlags.enableProductReceba && {
            label: "Receba",
            key: "receba",
            icon: <LikeOutlined style={{ color: "#8A92A6" }} />,
          }) as ItemType,
          ((featureFlags.enablePrivateConsigLoan ||
            featureFlags.enablePrivateConsigCef) && {
            label: "Consignado Privado",
            key: "consignado-privado",
            icon: <DollarCircleOutlined style={{ color: "#8A92A6" }} />,
          }) as ItemType,
          (featureFlags.enablePrivateConsigV2 && {
            label: "Consignado Privado 2",
            key: "v2/consignado-privado",
            icon: <DollarCircleOutlined style={{ color: "#8A92A6" }} />,
          }) as ItemType,
        ],
      },
      {
        label: "Contratos",
        key: "contratos",
        icon: <DesktopOutlined style={{ color: "#8A92A6" }} />,
      },
      {
        label: "Portabilidade Ataque",
        key: "portabilidade-ataque",
        icon: <ScanOutlined style={{ color: "#8A92A6" }} />,
      },
    ];

    if (isTrainingTabEnabled()) {
      fullMenu.push({
        label: "Treinamentos",
        key: "treinamentos",
        icon: <ReadOutlined style={{ color: "#8A92A6" }} />,
      });
    }

    if (
      featureFlags.enableDoNotDisturbTab ||
      featureFlags.enableDoNotDisturbTabCef
    ) {
      fullMenu.push({
        label: "Não Me Perturbe",
        key: "nao-me-perturbe",
        icon: <AudioMutedOutlined style={{ color: "#8A92A6" }} />,
      });
    }

    if (hasUsersCrud) {
      fullMenu.push(
        {
          label: "Usuários",
          key: "usuarios",
          icon: <TeamOutlined style={{ color: "#8A92A6" }} />,
        },
        {
          label: "Adicionar Usuário",
          key: "usuarios/novo",
          icon: <PlusOutlined style={{ color: "#8A92A6" }} />,
        },
      );
    }

    if (featureFlags.enableSupportMaterials) {
      fullMenu.push({
        label: "Materiais de Apoio",
        key: "materiais-de-apoio",
        icon: <FileDoneOutlined />,
      });
    }

    return getAllowedItemTypes(fullMenu);
  }, [getAllowedItemTypes, hasUsersCrud, BENEFIT_CARD_NAME]);

  const handleClick = ({ key }: { key: string }) => {
    const isJourneyEnd = pathname.includes(key) && !hasJourney;
    if (isJourneyEnd) return void navigate(homeRoute);
    void navigate(`/${key}`);
  };

  return (
    <Container>
      <Content>
        <LogoLink to={homeRoute}>
          <Logo icon={screens.xs} variant="header" />
        </LogoLink>
        <CorbanSelector />
        <Menu
          mode="inline"
          items={menuItems}
          onClick={handleClick}
          selectedKeys={[currentRoute]}
          openKeys={openKeys}
          onOpenChange={setOpenKeys}
        />
      </Content>

      <Footer>
        <Link to="/termos-de-uso">
          <Text>Termos de uso</Text>
        </Link>
        <Link to="/politica-de-privacidade">
          <Text>Política de privacidade</Text>
        </Link>
      </Footer>
    </Container>
  );
};
